/**
 * ResellersListPage — table of all channel partner profiles (resellers + freelancers + referral codes).
 *
 * Features:
 * - Three tabs: Resellers, Freelancers, Codigos de Referido
 * - Debounced search by user name / email / company name (300ms) — resellers/freelancers only
 * - Contract status filter dropdown — resellers/freelancers only
 * - Active status filter (Todos / Activos / Inactivos)
 * - Click row to navigate to /resellers/:id or /freelancers/:id
 * - "Nuevo Revendedor" / "Nuevo Freelancer" / "Nuevo Codigo" dialog forms
 * - Contract status badge (Borrador / En progreso / Firmado)
 * - Referral codes tab: table with Code, Description, Discount, Stripe Coupon,
 *   Owner, Reseller, Uses/Max, Expires, Active
 */

import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  useResellers,
  useFreelancers,
  useCreateReseller,
  useCreateFreelancer,
  type CreateResellerDTO,
  type CreateFreelancerDTO,
  type ResellerRow,
  type FreelancerRow,
} from '@/hooks/useResellers'
import {
  useReferralCodes,
  useCreateReferralCode,
  type CreateReferralCodeDTO,
  type ReferralCodeRow,
} from '@/hooks/useReferralCodes'

// ── Helpers ───────────────────────────────────────────────────────────────────

const CONTRACT_STATUS_COLORS: Record<string, string> = {
  draft: 'bg-yellow-100 text-yellow-800',
  in_progress: 'bg-blue-100 text-blue-800',
  signed: 'bg-green-100 text-green-800',
}

const CONTRACT_STATUS_LABELS: Record<string, string> = {
  draft: 'Borrador',
  in_progress: 'En progreso',
  signed: 'Firmado',
}

function ContractStatusBadge({ status }: { status: string }) {
  const color = CONTRACT_STATUS_COLORS[status] ?? 'bg-gray-100 text-gray-600'
  const label = CONTRACT_STATUS_LABELS[status] ?? status
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${color}`}
    >
      {label}
    </span>
  )
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '-'
  return new Date(dateStr).toLocaleDateString('es-CO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

// ── Create Reseller Form ───────────────────────────────────────────────────────

interface CreateResellerFormProps {
  onSubmit: (data: CreateResellerDTO) => Promise<void>
  onCancel: () => void
  isSubmitting: boolean
  error: string | null
}

function CreateResellerForm({ onSubmit, onCancel, isSubmitting, error }: CreateResellerFormProps) {
  const [userId, setUserId] = useState('')
  const [companyName, setCompanyName] = useState('')
  const [commissionType, setCommissionType] = useState<'percentage' | 'fixed'>('percentage')
  const [whiteLabel, setWhiteLabel] = useState(false)
  const [territorialExclusivity, setTerritorialExclusivity] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSubmit({
      userId: userId.trim(),
      companyName: companyName.trim() || null,
      commissionType,
      whiteLabel,
      territorialExclusivity,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      <div>
        <label htmlFor="r-userId" className="mb-1 block text-sm font-medium text-gray-700">
          UUID del usuario (role=reseller) <span className="text-red-500">*</span>
        </label>
        <input
          id="r-userId"
          type="text"
          required
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
          className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <p className="mt-1 text-xs text-gray-400">El usuario debe tener role='reseller'.</p>
      </div>

      <div>
        <label htmlFor="r-company" className="mb-1 block text-sm font-medium text-gray-700">
          Nombre de empresa
        </label>
        <input
          id="r-company"
          type="text"
          value={companyName}
          onChange={(e) => setCompanyName(e.target.value)}
          placeholder="Ej. Distribuciones Lopez S.A.S."
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      <div>
        <label htmlFor="r-commType" className="mb-1 block text-sm font-medium text-gray-700">
          Tipo de comision
        </label>
        <select
          id="r-commType"
          value={commissionType}
          onChange={(e) => setCommissionType(e.target.value as 'percentage' | 'fixed')}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value="percentage">Porcentaje</option>
          <option value="fixed">Fijo</option>
        </select>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-2">
          <input
            id="r-whiteLabel"
            type="checkbox"
            checked={whiteLabel}
            onChange={(e) => setWhiteLabel(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="r-whiteLabel" className="text-sm text-gray-700">
            White label
          </label>
        </div>
        <div className="flex items-center gap-2">
          <input
            id="r-exclusivity"
            type="checkbox"
            checked={territorialExclusivity}
            onChange={(e) => setTerritorialExclusivity(e.target.checked)}
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
          />
          <label htmlFor="r-exclusivity" className="text-sm text-gray-700">
            Exclusividad territorial
          </label>
        </div>
      </div>

      <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting ? 'Creando...' : 'Crear revendedor'}
        </button>
      </div>
    </form>
  )
}

// ── Create Freelancer Form ─────────────────────────────────────────────────────

interface CreateFreelancerFormProps {
  onSubmit: (data: CreateFreelancerDTO) => Promise<void>
  onCancel: () => void
  isSubmitting: boolean
  error: string | null
}

function CreateFreelancerForm({ onSubmit, onCancel, isSubmitting, error }: CreateFreelancerFormProps) {
  const [userId, setUserId] = useState('')
  const [specialty, setSpecialty] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSubmit({
      userId: userId.trim(),
      specialty: specialty.trim() || null,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      <div>
        <label htmlFor="f-userId" className="mb-1 block text-sm font-medium text-gray-700">
          UUID del usuario (role=freelance) <span className="text-red-500">*</span>
        </label>
        <input
          id="f-userId"
          type="text"
          required
          value={userId}
          onChange={(e) => setUserId(e.target.value)}
          placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
          className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <p className="mt-1 text-xs text-gray-400">El usuario debe tener role='freelance'.</p>
      </div>

      <div>
        <label htmlFor="f-specialty" className="mb-1 block text-sm font-medium text-gray-700">
          Especialidad
        </label>
        <input
          id="f-specialty"
          type="text"
          value={specialty}
          onChange={(e) => setSpecialty(e.target.value)}
          placeholder="Ej. E-commerce, Restaurantes..."
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>

      <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting ? 'Creando...' : 'Crear freelancer'}
        </button>
      </div>
    </form>
  )
}

// ── Create Referral Code Form ──────────────────────────────────────────────────

interface CreateReferralCodeFormProps {
  onSubmit: (data: CreateReferralCodeDTO) => Promise<void>
  onCancel: () => void
  isSubmitting: boolean
  error: string | null
}

function CreateReferralCodeForm({
  onSubmit,
  onCancel,
  isSubmitting,
  error,
}: CreateReferralCodeFormProps) {
  const [code, setCode] = useState('')
  const [description, setDescription] = useState('')
  const [discountPercent, setDiscountPercent] = useState('0')
  const [stripeCouponId, setStripeCouponId] = useState('')
  const [ownerId, setOwnerId] = useState('')
  const [resellerId, setResellerId] = useState('')
  const [maxUses, setMaxUses] = useState('')
  const [expiresAt, setExpiresAt] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSubmit({
      code: code.trim().toUpperCase(),
      description: description.trim() || null,
      discountPercent: parseFloat(discountPercent) || 0,
      stripeCouponId: stripeCouponId.trim() || null,
      ownerId: ownerId.trim() || null,
      resellerId: resellerId.trim() || null,
      maxUses: maxUses ? parseInt(maxUses, 10) : null,
      expiresAt: expiresAt ? new Date(expiresAt).toISOString() : null,
    })
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{error}</div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <div className="col-span-2">
          <label htmlFor="rc-code" className="mb-1 block text-sm font-medium text-gray-700">
            Codigo <span className="text-red-500">*</span>
          </label>
          <input
            id="rc-code"
            type="text"
            required
            minLength={3}
            maxLength={50}
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            placeholder="Ej. PROMO10, RESELLER2024"
            className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div className="col-span-2">
          <label htmlFor="rc-description" className="mb-1 block text-sm font-medium text-gray-700">
            Descripcion
          </label>
          <input
            id="rc-description"
            type="text"
            maxLength={500}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Ej. 10% de descuento para nuevos clientes"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label htmlFor="rc-discount" className="mb-1 block text-sm font-medium text-gray-700">
            Descuento (%)
          </label>
          <input
            id="rc-discount"
            type="number"
            min={0}
            max={100}
            step={0.01}
            value={discountPercent}
            onChange={(e) => setDiscountPercent(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label htmlFor="rc-maxUses" className="mb-1 block text-sm font-medium text-gray-700">
            Max. usos (vacio = ilimitado)
          </label>
          <input
            id="rc-maxUses"
            type="number"
            min={1}
            value={maxUses}
            onChange={(e) => setMaxUses(e.target.value)}
            placeholder="Sin limite"
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label htmlFor="rc-stripeCoupon" className="mb-1 block text-sm font-medium text-gray-700">
            Stripe Coupon ID
          </label>
          <input
            id="rc-stripeCoupon"
            type="text"
            maxLength={255}
            value={stripeCouponId}
            onChange={(e) => setStripeCouponId(e.target.value)}
            placeholder="Ej. STRIPE_COUPON_ID"
            className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
          <p className="mt-1 text-xs text-gray-400">
            Se aplica automaticamente en suscripciones Stripe del cliente.
          </p>
        </div>

        <div>
          <label htmlFor="rc-expires" className="mb-1 block text-sm font-medium text-gray-700">
            Vencimiento
          </label>
          <input
            id="rc-expires"
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label htmlFor="rc-reseller" className="mb-1 block text-sm font-medium text-gray-700">
            UUID Revendedor (opcional)
          </label>
          <input
            id="rc-reseller"
            type="text"
            value={resellerId}
            onChange={(e) => setResellerId(e.target.value)}
            placeholder="UUID del revendedor"
            className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>

        <div>
          <label htmlFor="rc-owner" className="mb-1 block text-sm font-medium text-gray-700">
            UUID Propietario (opcional)
          </label>
          <input
            id="rc-owner"
            type="text"
            value={ownerId}
            onChange={(e) => setOwnerId(e.target.value)}
            placeholder="UUID del usuario propietario"
            className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting ? 'Creando...' : 'Crear codigo'}
        </button>
      </div>
    </form>
  )
}

// ── Page component ────────────────────────────────────────────────────────────

type TabType = 'resellers' | 'freelancers' | 'referral-codes'

const PAGE_SIZE = 25

export function ResellersListPage() {
  const navigate = useNavigate()

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('resellers')

  // Shared filter state (resellers/freelancers)
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [contractStatusFilter, setContractStatusFilter] = useState<string>('')
  const [activeFilter, setActiveFilter] = useState<string>('')
  const [offset, setOffset] = useState(0)

  // Modal state
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createError, setCreateError] = useState<string | null>(null)

  // Debounce search input (300ms)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search)
      setOffset(0)
    }, 300)
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [search])

  // Reset pagination on tab or filter change
  const handleFilterChange =
    (setter: React.Dispatch<React.SetStateAction<string>>) =>
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setter(e.target.value)
      setOffset(0)
    }

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab)
    setOffset(0)
    setSearch('')
    setDebouncedSearch('')
    setContractStatusFilter('')
    setActiveFilter('')
  }

  // Filters
  const channelFilters = {
    search: debouncedSearch.trim() || undefined,
    contract_status: contractStatusFilter || undefined,
    active: activeFilter === '' ? undefined : activeFilter === 'true',
    limit: PAGE_SIZE,
    offset,
  }

  const referralCodeFilters = {
    active: activeFilter === '' ? undefined : activeFilter === 'true',
    limit: PAGE_SIZE,
    offset,
  }

  // Data queries
  const {
    data: resellers = [],
    isLoading: resellersLoading,
    isError: resellersError,
  } = useResellers(activeTab === 'resellers' ? channelFilters : { limit: 0 })

  const {
    data: freelancers = [],
    isLoading: freelancersLoading,
    isError: freelancersError,
  } = useFreelancers(activeTab === 'freelancers' ? channelFilters : { limit: 0 })

  const {
    data: referralCodes = [],
    isLoading: referralCodesLoading,
    isError: referralCodesError,
  } = useReferralCodes(activeTab === 'referral-codes' ? referralCodeFilters : { limit: 0 })

  const createResellerMutation = useCreateReseller()
  const createFreelancerMutation = useCreateFreelancer()
  const createReferralCodeMutation = useCreateReferralCode()

  const isLoading =
    activeTab === 'resellers'
      ? resellersLoading
      : activeTab === 'freelancers'
        ? freelancersLoading
        : referralCodesLoading

  const isError =
    activeTab === 'resellers'
      ? resellersError
      : activeTab === 'freelancers'
        ? freelancersError
        : referralCodesError

  // Create handlers
  const handleCreateReseller = async (data: CreateResellerDTO) => {
    setCreateError(null)
    try {
      const newReseller = await createResellerMutation.mutateAsync(data)
      setShowCreateModal(false)
      navigate(`/resellers/${newReseller.id}`)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al crear el revendedor'
      setCreateError(message)
    }
  }

  const handleCreateFreelancer = async (data: CreateFreelancerDTO) => {
    setCreateError(null)
    try {
      const newFreelancer = await createFreelancerMutation.mutateAsync(data)
      setShowCreateModal(false)
      navigate(`/freelancers/${newFreelancer.id}`)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al crear el freelancer'
      setCreateError(message)
    }
  }

  const handleCreateReferralCode = async (data: CreateReferralCodeDTO) => {
    setCreateError(null)
    try {
      await createReferralCodeMutation.mutateAsync(data)
      setShowCreateModal(false)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Error al crear el codigo de referido'
      setCreateError(message)
    }
  }

  const isCreating =
    activeTab === 'resellers'
      ? createResellerMutation.isPending
      : activeTab === 'freelancers'
        ? createFreelancerMutation.isPending
        : createReferralCodeMutation.isPending

  const currentCount =
    activeTab === 'resellers'
      ? resellers.length
      : activeTab === 'freelancers'
        ? freelancers.length
        : referralCodes.length

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Canal de socios</h1>
        <button
          onClick={() => {
            setCreateError(null)
            setShowCreateModal(true)
          }}
          className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          {activeTab === 'resellers'
            ? 'Nuevo revendedor'
            : activeTab === 'freelancers'
              ? 'Nuevo freelancer'
              : 'Nuevo codigo'}
        </button>
      </div>

      {/* Tab bar */}
      <div className="mb-4 border-b border-gray-200">
        <nav className="-mb-px flex gap-6" aria-label="Tabs">
          <button
            onClick={() => handleTabChange('resellers')}
            className={`whitespace-nowrap pb-3 text-sm font-medium transition-colors ${
              activeTab === 'resellers'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Revendedores
          </button>
          <button
            onClick={() => handleTabChange('freelancers')}
            className={`whitespace-nowrap pb-3 text-sm font-medium transition-colors ${
              activeTab === 'freelancers'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Freelancers
          </button>
          <button
            onClick={() => handleTabChange('referral-codes')}
            className={`whitespace-nowrap pb-3 text-sm font-medium transition-colors ${
              activeTab === 'referral-codes'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'border-b-2 border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Codigos de Referido
          </button>
        </nav>
      </div>

      {/* Filters */}
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
        {activeTab !== 'referral-codes' && (
          <div className="flex-1">
            <input
              type="text"
              placeholder="Buscar por nombre, email o empresa..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full max-w-md rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        )}

        {activeTab !== 'referral-codes' && (
          <select
            value={contractStatusFilter}
            onChange={handleFilterChange(setContractStatusFilter)}
            className="rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="">Todos los contratos</option>
            <option value="draft">Borrador</option>
            <option value="in_progress">En progreso</option>
            <option value="signed">Firmado</option>
          </select>
        )}

        <select
          value={activeFilter}
          onChange={handleFilterChange(setActiveFilter)}
          className="rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        >
          <option value="">Todos</option>
          <option value="true">Activos</option>
          <option value="false">Inactivos</option>
        </select>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <p className="text-gray-500">
              {activeTab === 'resellers'
                ? 'Cargando revendedores...'
                : activeTab === 'freelancers'
                  ? 'Cargando freelancers...'
                  : 'Cargando codigos de referido...'}
            </p>
          </div>
        )}

        {isError && (
          <div className="flex items-center justify-center py-12">
            <p className="text-red-500">Error al cargar los datos</p>
          </div>
        )}

        {!isLoading && !isError && (
          <>
            {activeTab === 'resellers' ? (
              <ResellersTable
                resellers={resellers}
                onRowClick={(id) => navigate(`/resellers/${id}`)}
              />
            ) : activeTab === 'freelancers' ? (
              <FreelancersTable
                freelancers={freelancers}
                onRowClick={(id) => navigate(`/freelancers/${id}`)}
              />
            ) : (
              <ReferralCodesTable referralCodes={referralCodes} />
            )}

            {/* Pagination */}
            <div className="flex items-center justify-between border-t border-gray-200 bg-white px-6 py-3">
              <button
                onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                disabled={offset === 0}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Anterior
              </button>
              <span className="text-sm text-gray-500">
                Mostrando {offset + 1}–{offset + currentCount}
              </span>
              <button
                onClick={() => setOffset((o) => o + PAGE_SIZE)}
                disabled={currentCount < PAGE_SIZE}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Siguiente
              </button>
            </div>
          </>
        )}
      </div>

      {/* Create modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
            <div className="border-b border-gray-200 px-6 py-4">
              <h2 className="text-lg font-semibold text-gray-900">
                {activeTab === 'resellers'
                  ? 'Nuevo revendedor'
                  : activeTab === 'freelancers'
                    ? 'Nuevo freelancer'
                    : 'Nuevo codigo de referido'}
              </h2>
            </div>
            <div className="p-6">
              {activeTab === 'resellers' ? (
                <CreateResellerForm
                  onSubmit={handleCreateReseller}
                  onCancel={() => setShowCreateModal(false)}
                  isSubmitting={isCreating}
                  error={createError}
                />
              ) : activeTab === 'freelancers' ? (
                <CreateFreelancerForm
                  onSubmit={handleCreateFreelancer}
                  onCancel={() => setShowCreateModal(false)}
                  isSubmitting={isCreating}
                  error={createError}
                />
              ) : (
                <CreateReferralCodeForm
                  onSubmit={handleCreateReferralCode}
                  onCancel={() => setShowCreateModal(false)}
                  isSubmitting={isCreating}
                  error={createError}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Resellers table ───────────────────────────────────────────────────────────

interface ResellersTableProps {
  resellers: ResellerRow[]
  onRowClick: (id: string) => void
}

function ResellersTable({ resellers, onRowClick }: ResellersTableProps) {
  if (resellers.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-500">No hay revendedores</p>
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Usuario / Empresa
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Tipo comision
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Exclusividad
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Contrato
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Activo
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Creado
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {resellers.map((r) => (
            <tr
              key={r.id}
              onClick={() => onRowClick(r.id)}
              className="cursor-pointer hover:bg-gray-50"
            >
              <td className="whitespace-nowrap px-6 py-4">
                <div className="text-sm font-medium text-gray-900">
                  {r.user_name ?? r.user_id.slice(0, 8) + '…'}
                </div>
                {r.company_name && (
                  <div className="text-xs text-gray-400">{r.company_name}</div>
                )}
                {r.user_email && (
                  <div className="text-xs text-gray-400">{r.user_email}</div>
                )}
              </td>
              <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600 capitalize">
                {r.commission_type}
              </td>
              <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                {r.territorial_exclusivity ? 'Si' : 'No'}
              </td>
              <td className="whitespace-nowrap px-6 py-4">
                <ContractStatusBadge status={r.contract_status} />
              </td>
              <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                {r.active ? (
                  <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                    Activo
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                    Inactivo
                  </span>
                )}
              </td>
              <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                {formatDate(r.created_at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Freelancers table ─────────────────────────────────────────────────────────

interface FreelancersTableProps {
  freelancers: FreelancerRow[]
  onRowClick: (id: string) => void
}

function FreelancersTable({ freelancers, onRowClick }: FreelancersTableProps) {
  if (freelancers.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-500">No hay freelancers</p>
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Usuario
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Especialidad
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Contrato
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Activo
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Creado
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {freelancers.map((f) => (
            <tr
              key={f.id}
              onClick={() => onRowClick(f.id)}
              className="cursor-pointer hover:bg-gray-50"
            >
              <td className="whitespace-nowrap px-6 py-4">
                <div className="text-sm font-medium text-gray-900">
                  {f.user_name ?? f.user_id.slice(0, 8) + '…'}
                </div>
                {f.user_email && (
                  <div className="text-xs text-gray-400">{f.user_email}</div>
                )}
              </td>
              <td className="px-6 py-4 text-sm text-gray-600">
                {f.specialty ?? '-'}
              </td>
              <td className="whitespace-nowrap px-6 py-4">
                <ContractStatusBadge status={f.contract_status} />
              </td>
              <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-600">
                {f.active ? (
                  <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                    Activo
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                    Inactivo
                  </span>
                )}
              </td>
              <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                {formatDate(f.created_at)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ── Referral codes table ──────────────────────────────────────────────────────

interface ReferralCodesTableProps {
  referralCodes: ReferralCodeRow[]
}

function ReferralCodesTable({ referralCodes }: ReferralCodesTableProps) {
  if (referralCodes.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <p className="text-gray-500">No hay codigos de referido</p>
      </div>
    )
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Codigo
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Descripcion
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Descuento
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Stripe Coupon
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Propietario / Revendedor
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Usos / Max
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Vence
            </th>
            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Activo
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {referralCodes.map((rc) => (
            <tr key={rc.id} className="hover:bg-gray-50">
              <td className="whitespace-nowrap px-4 py-4">
                <span className="font-mono text-sm font-semibold text-gray-900">{rc.code}</span>
              </td>
              <td className="px-4 py-4 text-sm text-gray-600">
                {rc.description ?? '-'}
              </td>
              <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                {rc.discount_percent > 0 ? `${rc.discount_percent}%` : '-'}
              </td>
              <td className="whitespace-nowrap px-4 py-4">
                {rc.stripe_coupon_id ? (
                  <span className="font-mono text-xs text-blue-700">{rc.stripe_coupon_id}</span>
                ) : (
                  <span className="text-sm text-gray-400">-</span>
                )}
              </td>
              <td className="whitespace-nowrap px-4 py-4">
                {rc.owner_name && (
                  <div className="text-xs text-gray-600">{rc.owner_name}</div>
                )}
                {rc.reseller_name && (
                  <div className="text-xs text-gray-400">{rc.reseller_name}</div>
                )}
                {!rc.owner_name && !rc.reseller_name && (
                  <span className="text-sm text-gray-400">-</span>
                )}
              </td>
              <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-600">
                {rc.usage_count} / {rc.max_uses != null ? rc.max_uses : '∞'}
              </td>
              <td className="whitespace-nowrap px-4 py-4 text-sm text-gray-500">
                {formatDate(rc.expires_at)}
              </td>
              <td className="whitespace-nowrap px-4 py-4">
                {rc.active ? (
                  <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                    Activo
                  </span>
                ) : (
                  <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-600">
                    Inactivo
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
